import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import {
  InitiatePurchaseInput,
  InitiatePurchaseResult,
  JuspaySdkPayload,
  OrderStatus,
  Query,
  SubscriptionPlan,
  User,
  VerifyPurchaseResult,
} from "@onmo/graphql/graphql";
import {
  getAAASubscriptionGQL,
  getSubscriptionPlansGQL,
  initiatePurchaseGQL,
  meAAAGQL,
  verifyPurchaseGQL,
} from "../../graphql";
import {
  FC,
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../../components/common/Header/Header";
import { LoadingScreen } from "../../components/common/LoadingScreen/LoadingScreen";
import { PaymentDialog } from "../../components/common/PaymentDialog/PaymentDialog";
import { CircularProgress } from "../../components/core/CircularProgress/CircularProgress";
import { Icon } from "../../components/core/Icon/Icon";
import {
  PaymentStatus,
  PENDING_POLL_INTERVAL_MS,
} from "../../constants/payment";
import { PageRoutes } from "../../constants/routes";
import {
  getBillingRegion,
  hasBillingInfo,
  isBillingRequired,
} from "../../models/billing";
import { useIsMounted } from "../../hooks/useIsMounted";
import { AnalyticsGA4 } from "../../models/analyticsGA4";
import { PaymentStep } from "../../types/payment";
import JuspayCheckout from "./JuspayCheckout";
import { useStyles } from "./Payment.style";
import { isAndroidApp } from "../../utils/platform";
import { getFullscreenElement } from "../../models/screen";
import { isSafariOnDesktopMac } from "../../constants/system";
import { exitPageFullscreenSafari } from "@onmo/auth";
import { useBackNavigation } from "../../hooks/useBackNavigation";
import { isMobile } from "../../constants/dimension";

const buildReturnUrl = (planId: string) =>
  `${window.location.origin}${PageRoutes.Payment}?planId=${encodeURIComponent(
    planId,
  )}`;

const Payment: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refetch: refetchSubscription, data: dataSubscription } = useQuery<{
    me: User;
  }>(getAAASubscriptionGQL);
  const [searchParams] = useSearchParams();
  const planId = searchParams.get("planId");
  const redirectOrderId = searchParams.get("order_id");
  const goBack = useBackNavigation();

  const { data: plansData, loading: plansLoading } = useQuery<Query>(
    getSubscriptionPlansGQL,
    {
      variables: { input: { productId: "aaa" } },
      skip: !planId,
    },
  );
  const { data: meData, loading: meLoading } = useQuery<{ me: User }>(meAAAGQL);

  const plan = useMemo<SubscriptionPlan | undefined>(
    () =>
      plansData?.subscriptionPlans?.items.find(
        (p: SubscriptionPlan) => p.id === planId,
      ),
    [plansData, planId],
  );
  const isMissingRequiredBilling =
    isBillingRequired(getBillingRegion(plan)) &&
    !hasBillingInfo(meData?.me?.metadata?.billingInfo);

  const [step, setStep] = useState<PaymentStep>(
    redirectOrderId ? "verifying" : "initiating",
  );
  const [sdkPayload, setSdkPayload] = useState<JuspaySdkPayload | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [juspayReady, setJuspayReady] = useState(false);
  const orderIdRef = useRef("");
  const dispatchedRef = useRef(false);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMounted = useIsMounted();

  const cancelPoll = () => {
    localStorage.removeItem("activeLink");
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  useEffect(() => cancelPoll, []);

  const onClose = () => {
    cancelPoll();
    navigate(PageRoutes.Home, { replace: true });
  };

  const [initiatePurchase] = useMutation<
    { initiatePurchase: InitiatePurchaseResult },
    { input: InitiatePurchaseInput }
  >(initiatePurchaseGQL);

  const [verifyPurchase] = useMutation<
    { verifyPurchase: VerifyPurchaseResult },
    { input: { orderId: string } }
  >(verifyPurchaseGQL);

  const handleInitiate = useCallback(async () => {
    setStep("initiating");
    if (!plan?.id) {
      setErrorMessage("Plan information is missing. Please pick a plan again.");
      setStep("failed");
      return;
    }
    const inputPurchase = isAndroidApp
      ? { planId: plan.id }
      : { planId: plan.id, returnUrl: buildReturnUrl(plan.id) };

    try {
      const { data } = await initiatePurchase({
        variables: {
          input: inputPurchase,
        },
      });
      if (!isMounted.current) return;

      const result = data?.initiatePurchase;

      if (!result) throw new Error("No response from server");

      orderIdRef.current = result.orderId;

      if (isSafariOnDesktopMac() && getFullscreenElement()) {
        await exitPageFullscreenSafari();
      }

      if (result.sdkPayload) {
        setSdkPayload(result.sdkPayload);
        setStep("juspay");
        return;
      }

      if (result.paymentUrls?.iframe) {
        setIframeUrl(result.paymentUrls.iframe);
        setStep("iframe");
        return;
      }

      if (result.paymentUrls?.web) {
        window.location.href = result.paymentUrls.web;
        return;
      }

      throw new Error("No payment method in response");
    } catch (error) {
      if (!isMounted.current) return;
      setErrorMessage(
        error instanceof Error ? error.message : "Could not start payment",
      );
      setStep("failed");
    }
  }, [initiatePurchase, plan, isMounted]);

  const trackTransaction = useCallback(
    (event: "success" | "failure" | "cancel" | "processing") => {
      AnalyticsGA4.subscriptionTransaction(
        "paywall",
        event,
        plan?.metadata?.type || "plan_type",
        dataSubscription?.me?.subscription?.status,
        errorMessage,
      );
    },
    [plan, dataSubscription, errorMessage],
  );

  const verifyOrder = useCallback(
    async (orderId: string) => {
      cancelPoll();
      setStep("verifying");
      let pendingTracked = false;

      const poll = async () => {
        if (!isMounted.current) return;
        try {
          const { data } = await verifyPurchase({
            variables: { input: { orderId } },
          });
          if (!isMounted.current) return;

          const status = data?.verifyPurchase?.status;

          switch (status) {
            case OrderStatus.Success:
              trackTransaction("success");
              await refetchSubscription().catch((err) => {
                console.error("Failed to refetch subscription", err);
              });
              setStep("success");
              return;
            case OrderStatus.Failed:
              trackTransaction("failure");
              setErrorMessage(
                "Payment could not be completed. Please try again.",
              );
              setStep("failed");
              return;
            case OrderStatus.Cancelled:
              trackTransaction("cancel");
              setStep("cancelled");
              return;
            case OrderStatus.Pending:
              if (!pendingTracked) {
                trackTransaction("processing");
                pendingTracked = true;
                setStep("pending");
              }
              pollTimeoutRef.current = setTimeout(
                poll,
                PENDING_POLL_INTERVAL_MS,
              );
              return;
            default:
              setErrorMessage("Unexpected payment status.");
              setStep("failed");
          }
        } catch {
          if (!isMounted.current) return;
          pollTimeoutRef.current = setTimeout(poll, PENDING_POLL_INTERVAL_MS);
        }
      };

      poll();
    },
    [verifyPurchase, refetchSubscription, trackTransaction, isMounted],
  );

  const handlePaymentFailure = useCallback(() => {
    trackTransaction("failure");
    setErrorMessage("Payment was unsuccessful. Please try again.");
    setStep("failed");
  }, [trackTransaction]);

  const handlePaymentCancel = useCallback(() => {
    trackTransaction("cancel");
    setStep("cancelled");
  }, [trackTransaction]);

  const handleRetry = useCallback(() => {
    cancelPoll();
    setSdkPayload(null);
    setIframeUrl(null);
    setErrorMessage("");
    setJuspayReady(false);
    orderIdRef.current = "";
    handleInitiate();
  }, [handleInitiate]);

  useEffect(() => {
    if (dispatchedRef.current) return;
    if (redirectOrderId) {
      dispatchedRef.current = true;
      verifyOrder(redirectOrderId);
      return;
    }

    if (!planId) {
      dispatchedRef.current = true;
      navigate(PageRoutes.Home, { replace: true });
      return;
    }

    if (plansLoading || meLoading) return;

    if (!plan) {
      dispatchedRef.current = true;
      navigate(PageRoutes.Home, { replace: true });
      return;
    }

    if (isMissingRequiredBilling) {
      dispatchedRef.current = true;
      navigate(PageRoutes.Paywall, { replace: true });
      return;
    }

    dispatchedRef.current = true;
    handleInitiate();
  }, [
    redirectOrderId,
    planId,
    plansLoading,
    meLoading,
    isMissingRequiredBilling,
    plan,
    verifyOrder,
    handleInitiate,
    navigate,
  ]);

  const renderContent = () => {
    if (!redirectOrderId && (plansLoading || meLoading))
      return (
        <div className={classes.bg} role="status">
          <span className="visually-hidden">{t("LoadingScreen_Loading")}</span>
        </div>
      );
    switch (step) {
      case "initiating":
        return (
          <div className={classes.loadingOverlay}>
            <CircularProgress enableAnimation />
          </div>
        );

      case "verifying":
        return <LoadingScreen />;

      case "juspay":
        if (!sdkPayload) return null;
        return (
          <div className={classes.content}>
            {!isMobile && juspayReady && (
              <Header
                leftItems={
                  <Icon
                    aria-label={t("A11y_Back")}
                    name="icon-linear-arrow-left-3"
                    onClick={handlePaymentCancel}
                    id="close-button"
                    data-button-sound="back"
                  />
                }
                className={classes.header}
              />
            )}
            {!juspayReady && (
              <div className={classes.loadingOverlay}>
                <CircularProgress enableAnimation />
              </div>
            )}
            <JuspayCheckout
              sdkPayload={sdkPayload}
              orderId={orderIdRef.current}
              onVerify={verifyOrder}
              onFailure={handlePaymentFailure}
              onCancel={handlePaymentCancel}
              onReady={() => setJuspayReady(true)}
            />
          </div>
        );

      case "pending":
        return (
          <PaymentDialog
            status={PaymentStatus.PENDING}
            open={true}
            onClose={onClose}
          />
        );

      case "iframe":
        if (!iframeUrl) return null;
        return (
          <div className={classes.iframeContainer}>
            <Header
              leftItems={
                <Icon
                  aria-label={t("A11y_Close")}
                  name="icon-bold-close"
                  onClick={goBack}
                  id="close-button"
                  data-button-sound="back"
                />
              }
            />
            <iframe
              src={iframeUrl}
              title={t("Payment_Payment")}
              className={classes.iframe}
            />
          </div>
        );

      case "success":
        return (
          <PaymentDialog
            status={PaymentStatus.SUCCESS}
            open={true}
            onClose={onClose}
            onStartPlaying={async () => {
              await refetchSubscription().catch((err) => {
                console.error("Failed to refetch subscription", err);
              });
              navigate(PageRoutes.Home, { replace: true });
            }}
          />
        );

      case "failed":
        return (
          <PaymentDialog
            status={PaymentStatus.FAILED}
            open={true}
            onClose={onClose}
            onTryAgain={handleRetry}
          />
        );

      case "cancelled":
        return (
          <PaymentDialog
            status={PaymentStatus.CANCELLED}
            open={true}
            onClose={onClose}
            onTryAgain={handleRetry}
          />
        );

      default:
        return null;
    }
  };

  return renderContent();
};

export default Payment;
